import { Badge } from '@classgo/ui';
import { useNavigate } from '@tanstack/react-router';

import type { SchoolYearView } from '../../academic/types';
import { EntityList } from '../../components/entity-list';
import { YearForm } from '../../forms/year-form';
import { useT } from '../../i18n';
import { formatDate } from '../../lib/dates';

/**
 * Status badge of a year or a period.
 */
export const StatusBadge = ({ status }: { status: SchoolYearView['status'] }) => {
  const t = useT();
  const tone = status === 'active' ? 'success' : status === 'closed' ? 'gray' : 'info';
  return <Badge tone={tone}>{t(`years.status.${status}`)}</Badge>;
};

/**
 * School years; a row opens the year with its periods and grading scale.
 */
export const YearsScreen = () => {
  const t = useT();
  const navigate = useNavigate();
  return (
    <EntityList
      entity="years"
      title={t('years.title')}
      caption={t('years.caption')}
      createLabel={t('years.new')}
      editTitle={t('years.edit')}
      emptyTitle={t('years.empty.title')}
      emptyBody={t('years.empty.body')}
      onRowClick={(row) => void navigate({ to: '/anos/$yearId', params: { yearId: row.id } })}
      columns={[
        {
          key: 'name',
          header: t('years.column.name'),
          cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>,
        },
        {
          key: 'calendar',
          header: t('years.column.calendar'),
          cell: (row) => t(`years.calendar.${row.calendar}`),
        },
        {
          key: 'dates',
          header: t('years.column.dates'),
          cell: (row) =>
            t('years.detail.dates', { from: formatDate(row.startsOn), to: formatDate(row.endsOn) }),
        },
        {
          key: 'status',
          header: t('years.column.status'),
          cell: (row) => <StatusBadge status={row.status} />,
        },
      ]}
      renderForm={({ current, onDone }) => <YearForm current={current} onDone={onDone} />}
    />
  );
};
